import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Box,
    Paper,
    Typography,
    Button,
    CircularProgress,
    Alert,
    Chip,
    IconButton,
    Collapse,
    Stack,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Checkbox,
    Tooltip,
    alpha,
    useTheme,
} from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CloseIcon from '@mui/icons-material/Close';
import RefreshIcon from '@mui/icons-material/Refresh';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import QuotaBadge from './QuotaBadge';

const arborGreen = '#006D43';

const getSeverityProps = (severity, theme) => {
    const s = (severity || 'info').toLowerCase();
    if (s === 'warning') {
        return {
            color: theme.palette.warning.main || '#ed6c02',
            bgColor: alpha(theme.palette.warning.main || '#ed6c02', 0.08),
            borderColor: theme.palette.warning.main || '#ed6c02',
            icon: <WarningAmberIcon sx={{ color: theme.palette.warning.main || '#ed6c02' }} />,
            label: 'Warning',
        };
    }
    if (s === 'opportunity') {
        return {
            color: arborGreen,
            bgColor: alpha(arborGreen, 0.08),
            borderColor: arborGreen,
            icon: <TrendingUpIcon sx={{ color: arborGreen }} />,
            label: 'Opportunity',
        };
    }
    return {
        color: theme.palette.info.main || '#0288d1',
        bgColor: alpha(theme.palette.info.main || '#0288d1', 0.08),
        borderColor: theme.palette.info.main || '#0288d1',
        icon: <InfoOutlinedIcon sx={{ color: theme.palette.info.main || '#0288d1' }} />,
        label: 'Insight',
    };
};

const InsightCard = ({ item, index, onDismiss }) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const [expanded, setExpanded] = useState(false);
    const [checkedItems, setCheckedItems] = useState({});

    const severityProps = getSeverityProps(item.severity, theme);

    const toggleActionItem = (idx) => {
        setCheckedItems((prev) => ({
            ...prev,
            [idx]: !prev[idx],
        }));
    };

    return (
        <Paper
            elevation={0}
            sx={{
                p: { xs: 2, sm: 2.5 },
                borderRadius: 2,
                border: '1px solid',
                borderColor: severityProps.borderColor,
                bgcolor: severityProps.bgColor,
                position: 'relative',
                transition: 'box-shadow 0.2s',
                '&:hover': {
                    boxShadow: (tTheme) =>
                        tTheme.palette.mode === 'dark'
                            ? '0 4px 12px rgba(0,0,0,0.4)'
                            : '0 4px 12px rgba(0,0,0,0.06)',
                },
            }}
        >
            <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                    <Chip
                        icon={severityProps.icon}
                        label={t(severityProps.label)}
                        size="small"
                        sx={{
                            fontWeight: 700,
                            fontSize: '0.75rem',
                            bgcolor: alpha(severityProps.color, 0.12),
                            color: severityProps.color,
                            border: `1px solid ${alpha(severityProps.color, 0.3)}`,
                            '& .MuiChip-icon': { color: 'inherit' },
                        }}
                    />
                    {item.category && (
                        <Chip
                            label={t(item.category)}
                            size="small"
                            variant="outlined"
                            sx={{
                                fontSize: '0.75rem',
                                color: 'text.secondary',
                                borderColor: 'divider',
                                textTransform: 'capitalize',
                            }}
                        />
                    )}
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, color: 'text.primary', lineHeight: 1.3 }}>
                        {item.title}
                    </Typography>
                </Box>

                {onDismiss && (
                    <Tooltip title={t("Dismiss this insight")} arrow>
                        <IconButton
                            size="small"
                            onClick={() => onDismiss(index)}
                            sx={{
                                color: 'text.disabled',
                                p: 0.5,
                                flexShrink: 0,
                                mt: -0.5,
                                mr: -0.5,
                                '&:hover': {
                                    color: 'text.primary',
                                    bgcolor: alpha(theme.palette.action.hover, 0.1),
                                },
                            }}
                        >
                            <CloseIcon fontSize="small" sx={{ fontSize: '1rem' }} />
                        </IconButton>
                    </Tooltip>
                )}
            </Box>

            <Typography variant="body2" sx={{ color: 'text.secondary', mt: 1, lineHeight: 1.5, wordBreak: 'break-word' }}>
                {item.summary}
            </Typography>

            {item.detail && (
                <Box sx={{ mt: 1.5 }}>
                    <Button
                        size="small"
                        onClick={() => setExpanded((prev) => !prev)}
                        endIcon={expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                        sx={{
                            p: 0,
                            minWidth: 0,
                            textTransform: 'none',
                            fontWeight: 600,
                            fontSize: '0.8rem',
                            color: 'primary.main',
                            '&:hover': { bgcolor: 'transparent', textDecoration: 'underline' },
                        }}
                    >
                        {expanded ? t('Hide Analysis') : t('View Full Analysis & Action Items')}
                    </Button>

                    <Collapse in={expanded} timeout="auto" unmountOnExit>
                        <Box sx={{ mt: 1.5, pt: 1.5, borderTop: '1px dashed', borderColor: 'divider' }}>
                            <Box
                                sx={{
                                    p: 2,
                                    borderRadius: 1.5,
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    bgcolor: 'transparent',
                                    mb: 1.5,
                                }}
                            >
                                <Typography variant="body2" sx={{ color: 'text.primary', whiteSpace: 'pre-line', lineHeight: 1.6 }}>
                                    {item.detail}
                                </Typography>
                            </Box>

                            {item.action_items && item.action_items.length > 0 && (
                                <Box
                                    sx={{
                                        p: 2,
                                        borderRadius: 1.5,
                                        border: '1px solid',
                                        borderColor: 'divider',
                                        bgcolor: 'transparent',
                                    }}
                                >
                                    <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                                        {t('Recommended Action Steps')}
                                    </Typography>
                                    <List dense disablePadding sx={{ mt: 0.75 }}>
                                        {item.action_items.map((action, actIdx) => {
                                            const isChecked = Boolean(checkedItems[actIdx]);
                                            return (
                                                <ListItem
                                                    key={actIdx}
                                                    disablePadding
                                                    sx={{
                                                        py: 0.35,
                                                        cursor: 'pointer',
                                                    }}
                                                    onClick={() => toggleActionItem(actIdx)}
                                                >
                                                    <ListItemIcon sx={{ minWidth: 28 }}>
                                                        <Checkbox
                                                            size="small"
                                                            checked={isChecked}
                                                            sx={{
                                                                p: 0.5,
                                                                color: arborGreen,
                                                                '&.Mui-checked': { color: arborGreen },
                                                            }}
                                                        />
                                                    </ListItemIcon>
                                                    <ListItemText
                                                        primary={action}
                                                        primaryTypographyProps={{
                                                            variant: 'body2',
                                                            sx: {
                                                                textDecoration: isChecked ? 'line-through' : 'none',
                                                                color: isChecked ? 'text.disabled' : 'text.primary',
                                                                fontSize: '0.85rem',
                                                            },
                                                        }}
                                                    />
                                                </ListItem>
                                            );
                                        })}
                                    </List>
                                </Box>
                            )}
                        </Box>
                    </Collapse>
                </Box>
            )}
        </Paper>
    );
};

const AdvisorInsightsSection = ({
    ledgerId,
    scope = 'BUDGET',
    year,
    title = 'AI Financial Insights',
    subtitle,
    fullWidth = false,
    defaultCollapsed = true,
}) => {
    const { t, i18n } = useTranslation();
    const { api } = useAuth();
    const { showToast } = useToast();
    const theme = useTheme();

    const [cachedData, setCachedData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [fetchingCache, setFetchingCache] = useState(true);
    const [error, setError] = useState(null);
    const [isCollapsed, setIsCollapsed] = useState(defaultCollapsed);

    // Fetch cached insight
    const fetchCached = useCallback(async () => {
        if (!ledgerId) return;
        setFetchingCache(true);
        setError(null);
        try {
            const params = { scope };
            if (scope !== 'INVESTMENTS' && year) {
                params.year = year;
            }
            const res = await api.get(`/ledger/ledgers/${ledgerId}/advisor`, { params });
            setCachedData(res.data);
        } catch (err) {
            if (err.response?.status === 404) {
                // No cached insight found
                setCachedData(null);
            } else {
                console.error('Failed to load cached advisor insight', err);
            }
        } finally {
            setFetchingCache(false);
        }
    }, [api, ledgerId, scope, year]);

    useEffect(() => {
        fetchCached();
    }, [fetchCached]);

    // Generate new insights
    const handleGenerate = async () => {
        if (!ledgerId) return;
        setLoading(true);
        setError(null);
        try {
            const payload = {
                scope,
                year: scope === 'INVESTMENTS' ? null : year || new Date().getFullYear(),
            };
            const res = await api.post(`/ledger/ledgers/${ledgerId}/advisor`, payload);
            setCachedData(res.data);
            setIsCollapsed(false);
            if (showToast) showToast(t('AI insights generated successfully!'), 'success');
            // Trigger quota badge refresh
            window.dispatchEvent(new CustomEvent('quota-updated'));
        } catch (err) {
            console.error('Failed to generate advisor insights', err);
            const msg = err.response?.data?.detail || t('Failed to generate insights. Please try again.');
            setError(msg);
            if (showToast) showToast(msg, 'error');
        } finally {
            setLoading(false);
        }
    };

    // Dismiss a single insight item by its array index
    const handleDismissItem = async (indexToDismiss) => {
        if (!ledgerId || !cachedData?.id) return;
        try {
            await api.delete(`/ledger/ledgers/${ledgerId}/advisor/${cachedData.id}`, {
                params: { item_index: indexToDismiss },
            });
            setCachedData((prev) => {
                if (!prev) return null;
                const updated = (prev.insights || []).filter((_, i) => i !== indexToDismiss);
                if (updated.length === 0) return null;
                return { ...prev, insights: updated };
            });
            if (showToast) showToast(t('Insight dismissed.'), 'info');
        } catch (err) {
            console.error('Failed to dismiss insight item', err);
            if (showToast) showToast(t('Failed to dismiss insight.'), 'error');
        }
    };

    // Dismiss (soft-delete) all cached insights for this scope
    const handleDismissAll = async () => {
        if (!ledgerId || !cachedData?.id) return;
        try {
            await api.delete(`/ledger/ledgers/${ledgerId}/advisor/${cachedData.id}`);
            setCachedData(null);
            if (showToast) showToast(t('All insights dismissed.'), 'info');
        } catch (err) {
            console.error('Failed to dismiss insight', err);
            if (showToast) showToast(t('Failed to dismiss insights.'), 'error');
        }
    };

    const defaultSubtitle = {
        BUDGET: 'AI analysis on spending patterns, budget limit overages, and savings opportunities.',
        INVESTMENTS: 'AI analysis on portfolio balance, sector concentration, and market context.',
        HOLISTIC: 'Holistic wealth analysis and recommendations for extra investable cash.',
    }[scope] || 'AI-powered financial recommendations.';

    const buttonLabel = {
        BUDGET: 'Analyze Budget & Spending',
        INVESTMENTS: 'Analyze Portfolio',
        HOLISTIC: 'Generate Full Holistic Analysis',
    }[scope] || 'Generate Insights';

    if (fetchingCache) {
        return (
            <Paper
                elevation={0}
                sx={{
                    p: 2.5,
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: 'divider',
                    bgcolor: alpha(arborGreen, 0.02),
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 1.5,
                    minHeight: 100,
                }}
            >
                <CircularProgress size={20} sx={{ color: arborGreen }} />
                <Typography variant="body2" color="text.secondary">
                    {t('Checking AI advisor insights...')}
                </Typography>
            </Paper>
        );
    }

    // No cached insights available -> Show Call-to-action banner
    if (!cachedData) {
        return (
            <Paper
                elevation={0}
                sx={{
                    p: { xs: 2.5, sm: 3 },
                    borderRadius: 2.5,
                    border: '1px solid',
                    borderColor: alpha(arborGreen, 0.25),
                    background: `linear-gradient(135deg, ${alpha(arborGreen, 0.05)} 0%, ${alpha(theme.palette.background.paper, 0.9)} 100%)`,
                    position: 'relative',
                    overflow: 'hidden',
                }}
            >
                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'flex-start', sm: 'center' }, justifyContent: 'space-between', gap: 2 }}>
                    <Box sx={{ maxWidth: 650 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                            <AutoAwesomeIcon sx={{ color: arborGreen, fontSize: 20 }} />
                            <Typography variant="h6" sx={{ fontWeight: 700, color: 'text.primary', fontSize: '1.05rem' }}>
                                {typeof title === 'string' ? t(title) : title}
                            </Typography>
                        </Box>
                        <Typography variant="body2" color="text.secondary">
                            {subtitle ? (typeof subtitle === 'string' ? t(subtitle) : subtitle) : t(defaultSubtitle)}
                        </Typography>
                    </Box>

                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: { xs: 'stretch', sm: 'flex-end' }, gap: 1, width: { xs: '100%', sm: 'auto' } }}>
                        <Button
                            variant="contained"
                            onClick={handleGenerate}
                            disabled={loading}
                            startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <AutoAwesomeIcon />}
                            sx={{
                                bgcolor: arborGreen,
                                color: '#fff',
                                fontWeight: 700,
                                px: 2.5,
                                py: 1,
                                borderRadius: 1.5,
                                textTransform: 'none',
                                boxShadow: '0 2px 8px rgba(0, 109, 67, 0.25)',
                                '&:hover': {
                                    bgcolor: '#005232',
                                },
                            }}
                        >
                            {loading ? t('Analyzing with AI...') : t(buttonLabel)}
                        </Button>
                        <QuotaBadge featureKey="ai_advice" compact />
                    </Box>
                </Box>

                {error && (
                    <Alert severity="error" sx={{ mt: 2, borderRadius: 1.5 }}>
                        {error}
                    </Alert>
                )}
            </Paper>
        );
    }

    // Cached insights exist -> render insight cards
    const insights = cachedData.insights || [];
    const generatedDate = cachedData.generated_at ? new Date(cachedData.generated_at).toLocaleString(i18n.language) : null;

    return (
        <Paper
            elevation={0}
            sx={{
                p: { xs: 2, sm: 3 },
                borderRadius: 2.5,
                border: '1px solid',
                borderColor: alpha(arborGreen, 0.3),
                bgcolor: alpha(arborGreen, 0.02),
                width: '100%',
                maxWidth: '100%',
                boxSizing: 'border-box',
                overflow: 'hidden'
            }}
        >
            {/* Header Toolbar */}
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', sm: 'row' },
                    alignItems: { xs: 'flex-start', sm: 'center' },
                    justifyContent: 'space-between',
                    gap: 1.5,
                    mb: isCollapsed ? 0 : 2.5,
                    cursor: 'pointer',
                    userSelect: 'none',
                    width: '100%',
                }}
                onClick={() => setIsCollapsed((prev) => !prev)}
            >
                <Box sx={{ width: { xs: '100%', sm: 'auto' }, minWidth: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                        <IconButton
                            size="small"
                            onClick={(e) => {
                                e.stopPropagation();
                                setIsCollapsed((prev) => !prev);
                            }}
                            sx={{ color: arborGreen, p: 0.25 }}
                        >
                            {isCollapsed ? <ExpandMoreIcon /> : <ExpandLessIcon />}
                        </IconButton>
                        <AutoAwesomeIcon sx={{ color: arborGreen, fontSize: 20 }} />
                        <Typography variant="h6" sx={{ fontWeight: 700, color: 'text.primary', fontSize: '1.1rem', wordBreak: 'break-word' }}>
                            {typeof title === 'string' ? t(title) : title}
                        </Typography>
                        <Chip
                            label={`${insights.length} ${insights.length === 1 ? t('Insight') : t('Insights')}`}
                            size="small"
                            sx={{
                                height: 22,
                                fontWeight: 700,
                                fontSize: '0.7rem',
                                bgcolor: alpha(arborGreen, 0.12),
                                color: arborGreen,
                            }}
                        />
                    </Box>
                    {generatedDate && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25, pl: 4, wordBreak: 'break-word' }}>
                            {t('Analyzed on')} {generatedDate}
                        </Typography>
                    )}
                </Box>

                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        flexWrap: 'wrap',
                        width: { xs: '100%', sm: 'auto' },
                        justifyContent: { xs: 'flex-start', sm: 'flex-end' }
                    }}
                    onClick={(e) => e.stopPropagation()}
                >
                    <QuotaBadge featureKey="ai_advice" compact />
                    <Button
                        size="small"
                        variant="outlined"
                        onClick={handleGenerate}
                        disabled={loading}
                        startIcon={loading ? <CircularProgress size={14} /> : <RefreshIcon />}
                        sx={{
                            textTransform: 'none',
                            fontWeight: 600,
                            fontSize: '0.8rem',
                            borderColor: alpha(arborGreen, 0.4),
                            color: arborGreen,
                            '&:hover': {
                                borderColor: arborGreen,
                                bgcolor: alpha(arborGreen, 0.05),
                            },
                        }}
                    >
                        {loading ? t('Regenerating...') : t('Regenerate')}
                    </Button>
                    <Tooltip title={t("Dismiss all insights")} arrow>
                        <IconButton
                            size="small"
                            onClick={handleDismissAll}
                            sx={{
                                color: 'text.secondary',
                                '&:hover': { color: 'error.main', bgcolor: alpha(theme.palette.error.main, 0.08) },
                            }}
                        >
                            <CloseIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                </Box>
            </Box>

            {error && (
                <Alert severity="error" sx={{ mb: 2, borderRadius: 1.5 }}>
                    {error}
                </Alert>
            )}

            {/* Collapsible List of Insight Cards */}
            <Collapse in={!isCollapsed} timeout="auto" unmountOnExit>
                {insights.length === 0 ? (
                    <Alert severity="info" sx={{ borderRadius: 1.5 }}>
                        {t("No specific warnings or opportunities detected for this period. Your finances look well-managed!")}
                    </Alert>
                ) : (
                    <Stack spacing={2} sx={{ mt: 1 }}>
                        {insights.map((item, idx) => (
                            <InsightCard
                                key={idx}
                                item={item}
                                index={idx}
                                onDismiss={handleDismissItem}
                            />
                        ))}
                    </Stack>
                )}
            </Collapse>
        </Paper>
    );
};

export default AdvisorInsightsSection;
